import { ModelType, ModelTypeType } from './data-qa-constants';
import { ModelWithStages } from './hooks/useAllModelStages';

const EXCEL_SHEET_NAME_LIMIT = 31;

// Spelled-out model names (not channel-export-utils' short MODEL_TYPE_ALIAS) -- in the
// one-sheet-per-model exports, the sheet name is the only place a reader learns which
// model a tab belongs to.
export const EXPORT_MODEL_SLUG: Record<ModelTypeType, string> = {
  [ModelType.DTC]: 'dtc',
  [ModelType.COMMERCE]: 'commerce',
  [ModelType.GEO]: 'geo',
  [ModelType.BRAND_EQUITY]: 'brand_equity'
};

// "<company>_<kind>_<modelPart>". The company prefix is what gets truncated to fit
// Excel's 31-char sheet-name limit, so the model part -- the whole point of the name --
// always survives.
export const exportSheetName = (company: string, kind: string, modelPart: string): string => {
  const suffix = `_${kind}_${modelPart}`;
  return `${company.slice(0, Math.max(0, EXCEL_SHEET_NAME_LIMIT - suffix.length))}${suffix}`;
};

// The model part of each model's sheet name, index-aligned with `models`. Two models of
// the same type (e.g. two regional geoMMMs) would otherwise collide, so those get the
// regional scope appended, falling back to client name if that ties too. A tie that
// survives both (no scope, no client name) is left to exportMultiSheetToExcel, which
// suffixes duplicate sheet names with a counter.
export const modelSheetParts = (models: ModelWithStages[]): string[] => {
  const countByType = new Map<ModelTypeType, number>();
  models.forEach(m => countByType.set(m.modelType, (countByType.get(m.modelType) ?? 0) + 1));
  const usedModelParts = new Set<string>();

  return models.map(model => {
    let modelPart = EXPORT_MODEL_SLUG[model.modelType];
    if ((countByType.get(model.modelType) ?? 0) > 1) {
      const { regionalScope, clientName } = model.stages[0].config;
      const scope = regionalScope?.trim().toLowerCase();
      if (scope) {
        modelPart = `${modelPart}_${scope}`;
      }
      if (usedModelParts.has(modelPart) && clientName?.trim()) {
        modelPart = `${modelPart}_${clientName.trim().toLowerCase().replace(/\s+/g, '')}`;
      }
    }
    usedModelParts.add(modelPart);
    return modelPart;
  });
};
